import {
  ExceedsNonPrimaryVolumeByInsight,
  ExceedsNonPrimaryVolumeByTimeInsight,
  ExceedsNonPrimaryVolumeStatisticsSearchToolbar,
  ResizeableContent,
  RouteBreadcrumbs,
  ThemedPage,
  useApp
} from "@ots-frontend/ots-ui";
import {
  Card,
  Container,
  Grid,
  Group,
  LoadingOverlay,
  Menu,
  Radio,
  rem,
  Stack,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
  adjustToTimeZone,
  cargoSubtype,
  EntitySearchCriteria,
  ExceedsNonPrimaryVolumeStatisticsSearchCriteria,
  exceedsNonPrimaryVolumeStatisticsSelector,
  getDefaultTimeFrame,
  TimeGroup,
  timeGroup,
  useSearchExceedsNonPrimaryVolumeStatisticsQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {useDispatch} from "react-redux";
import {
  getExceedsNonPrimaryVolumeStatisticsSearchPageFilter
} from "../../store/exceeds-non-primary-volume-statistics-search-page/selectors";
import {
  setExceedsNonPrimaryVolumeStatisticsSearchPageFilter
} from "../../store/exceeds-non-primary-volume-statistics-search-page/actions";

export const ExceedsNonPrimaryVolumeStatisticsSearchPage = () => {

  const dispatch = useDispatch()
  const {t} = useTranslation(["translation", "common"]);
  const {regionId, appCategory} = useApp()

  const filter = useAppSelector(getExceedsNonPrimaryVolumeStatisticsSearchPageFilter)

  const [display, setDisplay] = useState<"volume" | "count">("count")
  const [grouping, setGrouping] = useState<string>("route")
  const [chartType, setChartType] = useState<string>("pie")

  const selectExceedsNonPrimaryVolumeStatistics = useMemo(() => {
    return exceedsNonPrimaryVolumeStatisticsSelector()
  }, []);

  const volumeInventoryCriteria: ExceedsNonPrimaryVolumeStatisticsSearchCriteria | any = useMemo(() => {
    if (filter?.fromDttm && regionId) {
      const defaultTimeFrame = getDefaultTimeFrame(filter.fromDttm, filter.toDttm)

      let criteriaTimeGroup: TimeGroup
      if (defaultTimeFrame === "MONTH" || defaultTimeFrame === "WEEK" || defaultTimeFrame === "DAY") {
        criteriaTimeGroup = timeGroup.daily
      } else {
        criteriaTimeGroup = timeGroup.hourly
      }

      return {
        fromDttm: adjustToTimeZone(filter.fromDttm).getTime(),
        toDttm: filter.toDttm ? adjustToTimeZone(filter.toDttm).getTime() : undefined,
        timeGroup: criteriaTimeGroup.code,
        regionId: regionId,
        cargoSubtypes: [(appCategory?.cargoSubtype ?? cargoSubtype.water).code],
        entityCriteria: filter.entityCriteria,
      }
    } else {
      return skipToken
    }
  }, [filter?.fromDttm, filter?.toDttm, filter?.entityCriteria, regionId, appCategory?.cargoSubtype])

  const {snapshots, isLoading, refetch} = useSearchExceedsNonPrimaryVolumeStatisticsQuery(volumeInventoryCriteria, {
    selectFromResult: (result) => ({
      ...result,
      snapshots: selectExceedsNonPrimaryVolumeStatistics(result),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const onFilterChange = useCallback((value: {
    fromDate: Date,
    toDate: Date | undefined,
    entityType: string | undefined,
    entityIds: string[] | undefined
  }) => {
    let entityCriteria: EntitySearchCriteria | undefined = undefined
    if (value.entityType) {
      entityCriteria = {
        ids: value.entityIds,
        type: value.entityType
      }
    }

    dispatch(setExceedsNonPrimaryVolumeStatisticsSearchPageFilter({
      fromDttm: value.fromDate.getTime(),
      toDttm: value.toDate?.getTime(),
      entityCriteria: entityCriteria
    }));

    let grouping = "none"

    if (value.entityType) {
      if (value.entityType === "ROUTEGROUP") {
        grouping = "route_group"
      } else if (value.entityType === "ROUTE") {
        grouping = "route"
      } else if (value.entityType === "SITE") {
        grouping = "site"
      } else if (value.entityType === "COMPANY") {
        grouping = "truck_company"
      }
    }

    setGrouping(grouping)
  }, [dispatch]);

  useEffect(() => {
    let entityCriteria: EntitySearchCriteria | undefined = undefined

    if (grouping === "route_group") {
      entityCriteria = {
        type: "ROUTEGROUP"
      }
    } else if (grouping === "route") {
      entityCriteria = {
        type: "ROUTE"
      }
    } else if (grouping === "site") {
      entityCriteria = {
        type: "SITE"
      }
    } else if (grouping === "truck_company") {
      entityCriteria = {
        type: "COMPANY"
      }
    }

    if (entityCriteria?.type !== filter?.entityCriteria?.type) {
      dispatch(setExceedsNonPrimaryVolumeStatisticsSearchPageFilter({
        ...filter ?? {fromDttm: new Date().getTime()},
        entityCriteria: entityCriteria
      }));
    }

  }, [dispatch, grouping, filter])

  const onDisplayChange = useCallback((value: string) => {
    if (value === "volume" || value === "count") {
      setDisplay(value)
    } else {
      setDisplay("volume")
    }
  }, [])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <RouteBreadcrumbs p="sm"/>
              <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                <Group position="apart">
                  <Title
                    order={4}>{t("page.exceedsnonprimaryvolumestatisticssearch.title", "High Bottoms By", {ns: "translation"})}</Title>
                  <Menu
                    width={260}
                    position="bottom-end"
                    transitionProps={{transition: "pop-top-right"}}
                    withinPortal
                  >
                    <Menu.Target>
                      <UnstyledButton>
                        <Group spacing={7}>
                          <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                            Actions
                          </Text>
                          <IconChevronDown size={rem(12)} stroke={1.5}/>
                        </Group>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={() => refetch()}>
                        {t("commons.action.refresh", "Refresh", {ns: "common"})}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
                <ExceedsNonPrimaryVolumeStatisticsSearchToolbar
                  defaultValues={{
                    fromDate: filter?.fromDttm ? new Date(filter?.fromDttm) : undefined,
                    toDate: filter?.toDttm ? new Date(filter?.toDttm) : undefined,
                    entityType: filter?.entityCriteria?.type,
                    entityIds: filter?.entityCriteria?.ids,
                  }}
                  loading={isLoading}
                  onSubmit={onFilterChange}/>
              </Card>

              <LoadingOverlay visible={isLoading}/>

              <Container p="md" fluid={true}>
                <Grid>
                  <Grid.Col span={10}>
                    {chartType === "pie" &&
                      <ExceedsNonPrimaryVolumeByInsight
                        id={"search_exceeds_non_primary_by_chart"}
                        data={snapshots}
                        display={display}
                        tooltipPos={'left'}
                        containerProps={{
                          h: 400
                        }}/>
                    }
                    {chartType !== "pie" &&
                      <ExceedsNonPrimaryVolumeByTimeInsight
                        id={"search_exceeds_non_primary_by_time_chart"}
                        fromDttm={filter?.fromDttm ?? new Date().getTime()}
                        toDttm={filter?.toDttm}
                        category={appCategory}
                        data={snapshots}
                        display={display}
                        type={chartType}
                        stacked={{
                          enabled: true,
                          filled: true
                        }}
                        tooltipPos={'left'}
                        containerProps={{
                          h: 400
                        }}/>
                    }
                  </Grid.Col>
                  <Grid.Col span={2}>
                    <Stack>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Radio.Group
                          name="chartType"
                          value={chartType}
                          onChange={setChartType}>
                          <Stack mt="xs">
                            <Radio value="pie" label={"Pie"}/>
                            <Radio value="line" label={"Line"}/>
                            <Radio value="bar" label={"Bar"}/>
                          </Stack>
                        </Radio.Group>
                      </Card>
                      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                        <Radio.Group name="display" value={display} onChange={onDisplayChange}>
                          <Stack mt="xs">
                            <Radio value="count" label={"Count"}/>
                            <Radio value="volume" label={"Volume"}/>
                          </Stack>
                        </Radio.Group>
                      </Card>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Radio.Group
                          name="grouping"
                          value={grouping}
                          onChange={setGrouping}>
                          <Stack mt="xs">
                            <Radio value="none" label={t("common.insights.grouping.none", "None")}/>
                            <Radio value="route_group" label={t("common.insights.grouping.routegroup", "Route Group")}/>
                            <Radio value="route" label={t("common.insights.grouping.route", "Route")}/>
                            <Radio value="site" label={t("common.insights.grouping.truckcompany", "Site")}/>
                          </Stack>
                        </Radio.Group>
                      </Card>
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Container>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
